import type { TableFilterState } from '@ibsys/kit';
import {
  CONVERSATION_IDS_FILTER_ID,
  CONVERSATION_SELECTION_SOURCES,
  type ConversationSelection,
  type ConversationSelectionSource,
} from '../model/selection';

/**
 * Распознаёт внешнюю выборку диалогов в состоянии навигации.
 * В состоянии роутера страницы диалогов лежат и другие значения (например, строка
 * поисковых параметров списка), поэтому форма проверяется целиком.
 */
export const isConversationSelection = (state: unknown): state is ConversationSelection => {
  if (typeof state !== 'object' || state === null) return false;
  const { conversationIds, description, source } = state as Partial<ConversationSelection>;
  return (
    Array.isArray(conversationIds) &&
    conversationIds.every((id) => typeof id === 'number') &&
    typeof description === 'string' &&
    typeof source === 'string' &&
    (CONVERSATION_SELECTION_SOURCES as readonly string[]).includes(source)
  );
};

/**
 * Приводит сохранённый источник выборки к известному значению.
 * Неизвестное или отсутствующее (в т.ч. из настроек, сохранённых до появления
 * зон качества) трактуется как авто-инсайт — прежнее поведение.
 */
export const normalizeConversationSelectionSource = (
  value: string | undefined,
): ConversationSelectionSource =>
  (CONVERSATION_SELECTION_SOURCES as readonly string[]).includes(value ?? '')
    ? (value as ConversationSelectionSource)
    : 'autoInsight';

/**
 * Ключ выборки — её состав, не зависящий от порядка идентификаторов.
 * Одинаковый состав диалогов означает одну и ту же выборку, чем бы она ни была получена.
 */
export const buildConversationSelectionKey = (conversationIds: number[]): string =>
  [...conversationIds].sort((a, b) => a - b).join(',');

/**
 * Возвращает ключ выборки, лежащей среди фильтров таблицы.
 * `null` — фильтра выборки в состоянии таблицы нет.
 */
export const getConversationSelectionKey = (filters: TableFilterState): string | null => {
  const value = filters.find((filter) => filter.id === CONVERSATION_IDS_FILTER_ID)?.value;
  if (!Array.isArray(value) || value.length === 0) return null;
  return buildConversationSelectionKey(value.filter((id): id is number => typeof id === 'number'));
};
